/**
 * Owner: Masa Bayu
 * File: publication-ref.ts
 * Date:Time: 2026-09-15 19:18
 * Description: QXRead catalog publication reference foundation (≠ QXP truth).
 */
import { createQxreadId } from './id';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
import { assertNoForbiddenFoundationKey } from './authority';
export const QXREAD_PUBLICATION_REF_STATES = ['unbound', 'bound', 'broken', 'archived'];
export const QXREAD_VISIBILITIES = ['public', 'unlisted', 'restricted'];
function assertVisibility(value, label) {
    if (typeof value !== 'string' || !QXREAD_VISIBILITIES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_VISIBILITIES.join(', ')}`);
    }
    return value;
}
function assertRefState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_PUBLICATION_REF_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_PUBLICATION_REF_STATES.join(', ')}`);
    }
    return value;
}
export function createQxreadPublicationRef(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead publication ref');
    const publicationId = options.publicationId !== undefined ? options.publicationId : createQxreadId('qxrdpub');
    assertValidId(publicationId, 'QXRead publicationId');
    const title = assertOptionalTrimmed(options.title, 'QXRead publication title');
    if (title === null) {
        throw new Error('QXRead publication title is required');
    }
    const state = assertRefState(options.state ?? 'unbound', 'QXRead publication state');
    let qxpPublicationRef = null;
    if (options.qxpPublicationRef !== undefined && options.qxpPublicationRef !== null) {
        assertValidId(options.qxpPublicationRef, 'QXRead qxpPublicationRef');
        qxpPublicationRef = options.qxpPublicationRef;
    }
    if (state === 'bound' && qxpPublicationRef === null) {
        throw new Error('QXRead bound publication ref requires qxpPublicationRef');
    }
    if (state === 'unbound' && qxpPublicationRef !== null) {
        throw new Error('QXRead unbound publication must not set qxpPublicationRef');
    }
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead publication createdAt');
    let listedVersion = null;
    if (options.listedVersion !== undefined && options.listedVersion !== null) {
        listedVersion = assertOptionalTrimmed(options.listedVersion, 'QXRead listedVersion');
    }
    return Object.freeze({
        publicationId,
        qxpPublicationRef,
        title,
        visibility: assertVisibility(options.visibility ?? 'public', 'QXRead visibility'),
        state,
        listedVersion,
        createdAt,
    });
}
/** Catalog live/public metadata ≠ content access right. */
export function isCatalogBrowsable(ref) {
    return ((ref.state === 'bound' || ref.state === 'unbound') &&
        (ref.visibility === 'public' || ref.visibility === 'unlisted'));
}
/**
 * Bind catalog publication to a QXP publication identity reference.
 * Does not mutate QXP and does not grant content access.
 */
export function bindQxreadPublicationToQxp(ref, options) {
    if (ref.state === 'archived') {
        throw new Error('QXRead archived publication cannot be bound');
    }
    assertValidId(options.qxpPublicationRef, 'QXRead qxpPublicationRef');
    return createQxreadPublicationRef({
        publicationId: ref.publicationId,
        title: ref.title,
        visibility: ref.visibility,
        state: 'bound',
        qxpPublicationRef: options.qxpPublicationRef,
        listedVersion: options.listedVersion !== undefined ? options.listedVersion : ref.listedVersion,
        createdAt: ref.createdAt,
    });
}
//# sourceMappingURL=publication-ref.js.map